import { Component, OnInit, Injector, ElementRef, ViewChild, AfterContentChecked } from '@angular/core';
import { WorkOrdersService } from '../../../work-orders.service';
import { AddInfoCategoryService, GAWorkOrderService, DocumentService, AuthorizationService } from '../../../../ve.service';
import { DataGridTemplate } from '../../../../../../shared/data-grid/data-grid.template';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { FilterableService } from '../../../../../../shared/filterable-service';
import { ReportsService } from '../reports/reports.service';
import { GaUploadFileDialogComponent } from '../../dialogs/ga-upload-file-dialog/ga-upload-file-dialog.component';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { FileUploadService } from '../../../work-order-file-upload/file-upload.service';
import { CgpConfirmationDialogService } from '../../../../../../shared/cgp-confirmation-dialog/cgp-confirmation-dialog.service';

const TYPES = ['Not Required', 'Provided to VE', 'Attached'];
const CONTROLS = ['Id', 'CategoryDescription', 'File', 'Type'];

@Component({
  selector: 'ga-add-info-form',
  templateUrl: './ga-add-info-form.component.html',
  styleUrls: ['./ga-add-info-form.component.scss',
    '../../../../../ve/ve.component.scss',
    '../../../../../../shared/data-grid/data-grid.component.scss']
})
export class GaAddInfoFormComponent extends DataGridTemplate implements OnInit, AfterContentChecked {
  @ViewChild('printContainer', { static: true }) private readonly printContainerRef: ElementRef;
  printFormData = [];

  editForm: UntypedFormGroup;
  workOrderDetailsForm: UntypedFormGroup;
  workOrdersService: WorkOrdersService;
  addInfoCategoryService: AddInfoCategoryService;
  documentService: DocumentService;
  reportService: ReportsService;
  fileUploadService: FileUploadService;
  gaWorkOrderService: GAWorkOrderService;
  cgpConfirmationDialogService: CgpConfirmationDialogService;
  authService: AuthorizationService;
  typeService = new FilterableService(TYPES);
  columns: any[];
  matDialog: MatDialog;
  documentType: any;
  numRequired: any = 0;
  numRequiredSet: any = 0;
  isVisible: boolean;
  dataItem: any;

  constructor(injector: Injector) {
    super(injector);
    this.workOrdersService = injector.get(WorkOrdersService);
    this.addInfoCategoryService = injector.get(AddInfoCategoryService);
    this.documentService = injector.get(DocumentService);
    this.reportService = injector.get(ReportsService);
    this.fileUploadService = injector.get(FileUploadService);
    this.gaWorkOrderService = injector.get(GAWorkOrderService);
    this.authService = injector.get(AuthorizationService);
    this.matDialog = injector.get(MatDialog);
    this.cgpConfirmationDialogService = injector.get(CgpConfirmationDialogService);
  }

  ngOnInit() {
    this.editForm = this.workOrdersService.additionalInfoForm;
    this.workOrderDetailsForm = this.workOrdersService.workOrderDetailsForm;
    this.controls.excel = false;
    this.controls.delete = false;
    this.controls.edit = false;
    this.controls.add = false;
    this.isClickEdit = true;
    this.editOnselect = true;
    this.isNotificationsEnabled = false;
    this.gridState.sort = [{ field: 'DisplayOrder', dir: 'asc' }];
    this.gridState.take = 25;
    this.height = '100%';
    this.columnMenu = false;
    this.resizable = true;
    this.setColumns();

    const fileControl = this.editForm.get('File');
    fileControl.disable();

    this.editForm.get('Type').valueChanges
      .subscribe(x => {
        if (x === 'Attached') {
          fileControl.enable();
          this.isVisible = true;
        } else {
          fileControl.disable();
          this.isVisible = false;
        }
      });
  }

  ngAfterContentChecked() {
    this.setPrintData();
  }

  read(opts: any): Promise<any> {
    if (!this.data || this.data.length === 0) {
      return this.documentService.read('/GetDocumentTypes')
        .then(result => {
          this.documentType = result.filter(element => element.Name === 'AditionalInfo')[0];
          return this.getAddInfoCategories();
        });
    } else {
      return Promise.resolve(this.data);
    }
  }

  edit(dataItem: any): void {
    this.dataItem = dataItem;
    CONTROLS.forEach(k => {
      if (k === 'CategoryDescription') {
        this.editForm.get(k).setValidators([Validators.required, Validators.maxLength(255)]);
      }

      if (k === 'Type') {
        this.editForm.get(k).setValidators(Validators.required);
      }
    });
  }

  save(dataItem: any, isNew: boolean): Promise<any> {
    let index = this.data.map(x => x.Id).indexOf(dataItem.Id);
    Object.keys(dataItem).forEach(k => {
      this.data[index][k] = dataItem[k];
    });

    // Map selected and input Add Info Category data to main form
    // Main form is gaAddMultipleForm is user is adding multiple Work Orders
    // Main form is workOrderDetailsForm if user is adding a single new Work Order
    const ctrl = this.workOrdersService.getIsAddMultipleWO
      ? this.workOrdersService.gaAddMultipleForm.get('AddInfoCategories')
      : this.workOrdersService.workOrderDetailsForm.get('AddInfoCategories');
    const list: any[] = ctrl.value || [];

    index = list.map(x => x.AddInfoCategory.Id).indexOf(dataItem.Id);
    if (index !== -1) { list.splice(index, 1); }

    list.push({
      Description: dataItem.CategoryDescription,
      Type: dataItem.Type,
      AddInfoCategory: {
        Id: dataItem.Id,
        Description: dataItem.Description,
        IsRequired: dataItem.IsRequired
      },
    });

    ctrl.setValue(list);

    this.numRequiredSet = 0;
    this.data.forEach(item => {
      this.addRequiredSet(item);
    });
    this.workOrdersService.isAddInfoFormValid.next(this.numRequired === this.numRequiredSet);
    return Promise.resolve();
  }

  private addRequiredSet(item: any) {
    if (item.IsRequired && item.CategoryDescription && item.Type) {
      this.numRequiredSet++;
    }
  }

  create(parentEditDataItem: any): void {
    //this is kept empty on purpose
  }

  delete(dataItem: any): Promise<any> {
    return null;
  }

  getAddInfoCategories(): any {
    const url = this.workOrdersService.isViewOnlyWO ? '?activeOnly=false' : '?activeOnly=true';
    return this.addInfoCategoryService.read(url)
      .then(response => {
        this.countRequired(response);
        if (!this.workOrdersService.isNewWO || this.workOrdersService.isCopyWO) {
          this.mapToAddInfoObject(response);
        }
        return response;
      });
  }

  private mapToAddInfoObject(response: any): any {
    const woId = this.workOrderDetailsForm.get('Id').value;

    this.documentService.read(`/GetByDocType?workOrderId=${woId}&docTypeId=${this.documentType.Id}`)
      .then(docs => {

        this.workOrdersService.editData.AddInfoCategories.forEach(element => {
          const index = response.map(x => x.Id).indexOf(element.AddInfoCategory.Id);
          if (index > -1) {
            Object.keys(element).forEach(k => {
              if (k === 'Description') {
                response[index].CategoryDescription = element[k];
              }
              if (k === 'Type') {
                response[index].Type = element[k];
              }
            });
          }

          if (docs) {
            this.mapDocuments(docs, index, element, response);
          }

          this.setFormValidation(response);
        });
      });
  }

  private mapDocuments(docs: any, index: any, element: any, response: any) {
    const files: string[] = [];
    docs.forEach(doc => {
      if (doc.AddInfoCategoryId === element.AddInfoCategory.Id) {
        files.push(doc.Name);
      }
    });
    response[index].File = files.length > 1 ? files.join(', ') : files[0];
  }

  handleTypeChange(e) {
    if (this.dataItem.File && e !== 'Attached') {
      this.confirmTypeChange(this.dataItem);
    }
  }

  private confirmTypeChange(dataItem: any) {
    this.cgpConfirmationDialogService.showConfirmationDialog({
      title: 'Type Change Confirmation',
      message: `Are you sure you want to change the Type of ${dataItem.Description}? File attachment(s) will be lost.`,
      alert: 'danger',
      onYesClick: () => {
        this.removeFileAttachments(dataItem);
        this.cgpConfirmationDialogService.closeConfirmationDialog();
      },
      onNoClick: () => {
        this.editForm.get('Type').setValue(dataItem.Type);
        this.save(this.editForm.value, true);
        this.refresh();
        this.cgpConfirmationDialogService.closeConfirmationDialog();
      }
    });
  }

  private removeFileAttachments(dataItem: any) {
    const woId = this.workOrdersService.getIsAddMultipleWO
      ? this.workOrdersService.gaAddMultipleForm.get('Id').value
      : this.workOrdersService.workOrderDetailsForm.get('Id').value;

    this.isLoading.next(true);

    this.documentService.read('/GetByWorkOrderId?workOrderId=' + woId)
      .then(response => {
        response.forEach(element => {
          if (element.AddInfoCategoryId === dataItem.Id) {
            this.documentService.remove('?documentId=' + element.Id)
              .then(() => {
                this.editForm.get('File').setValue(null);
                this.save(this.editForm.value, true);
                this.refresh();
                this.isLoading.next(false);
              });
          }
        });
      });
  }

  setFormValidation(formData: any) {
    this.numRequiredSet = 0;
    formData.forEach(item => {
      this.addRequiredSet(item);
    });

    this.workOrdersService.isAddInfoFormValid.next(this.numRequired === this.numRequiredSet);
  }

  countRequired(response: any) {
    response.forEach(element => {
      if (element.IsRequired) { this.numRequired++; }
    });
  }

  private readonly setRequired = (dataItem) => {
    if (dataItem.IsRequired) {
      return 'required';
    }
    return null;
  }

  handleButtonClick = (e) => {
    const data = {
      fileType: 'AditionalInfo',
      workOrderId: this.workOrderDetailsForm.get('Id').value,
      addInfoCategoryId: this.editForm.get('Id').value
    };

    const dialogRef = this.matDialog.open(GaUploadFileDialogComponent, { data });
    dialogRef.afterClosed()
      .subscribe(result => {
        if (result) {
          const files: string[] = [];
          result.forEach(element => { files.push(element.name); });
          this.editForm.get('File').setValue(files.length > 1 ? files.join(', ') : files[0]);
          this.save(this.editForm.value, true);
        }
      });
  }

  setColumns() {
    this.columns = [
      {
        field: 'Description',
        width: 360,
        title: 'Category',
        editable: false,
        template: {
          name: 'AddInfoCategoryDescription',
          type: 'customStyle',
          setClass: this.setRequired,
        }
      },
      {
        field: 'CategoryDescription',
        width: 360,
        title: 'Description',
        editable: !this.workOrdersService.isViewOnlyWO
      },
      {
        field: 'Type',
        width: 360,
        title: 'Type',
        editable: !this.workOrdersService.isViewOnlyWO,
        template: {
          name: 'Type',
          type: 'dropdown',
          service: this.typeService,
          onChange: (e) => this.handleTypeChange(e)
        }
      },
      {
        field: 'File',
        width: 360,
        title: 'File Name',
        editable: !this.workOrdersService.isViewOnlyWO,
        template: {
          name: 'Upload/Download Files',
          type: 'button',
          isVisible: () => this.isVisible,
          handleButtonClick: (e) => this.handleButtonClick(e)
        }
      },
    ];
  }

  public setPrintData() {
    if (this.workOrdersService && this.workOrdersService.editData && this.workOrdersService.editData.AddInfoCategories) {
      this.printFormData = [...this.workOrdersService.editData.AddInfoCategories];
    } else {
      this.printFormData = [];
    }
  }

  public getPrintElement(): ElementRef {
    this.setPrintData();
    return this.printContainerRef;
  }
}
